import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NavLink,
  useLocation,
  useMatch,
  useResolvedPath,
} from 'react-router-dom';
import Tippy from '@tippyjs/react';

import { cn } from 'src/common/utils';
import {
  TabNavBar,
  tabNavBarLinkClassName,
} from 'src/common/components/TabNavBar';
import { Menu } from 'src/common/components/Menu';
import { useOverflowNav } from 'src/common/hooks/useOverflowNav';

/** Set to `'ellipsis'` to show "..." instead of localized "More" / "Mehr". */
const OVERFLOW_TRIGGER_VARIANT: 'text' | 'ellipsis' = 'text';

type TabItem = {
  to: string;
  label: string;
};

function isDashboardTabActive(pathname: string, tabTo: string): boolean {
  const lowerPath = pathname.toLowerCase();
  const lowerTab = tabTo.toLowerCase();
  const dashboardMarker = '/dashboard/';
  const dashboardIndex = lowerPath.indexOf(dashboardMarker);

  if (dashboardIndex === -1) {
    return false;
  }

  const afterDashboard = lowerPath.slice(
    dashboardIndex + dashboardMarker.length,
  );

  return (
    afterDashboard === lowerTab || afterDashboard.startsWith(`${lowerTab}/`)
  );
}

type ProjectDashboardTabNavProps = {
  isAdmin: boolean;
  isAdminOfAnyTechCrew: boolean;
};

export const ProjectDashboardTabNav = ({
  isAdmin,
  isAdminOfAnyTechCrew,
}: ProjectDashboardTabNavProps) => {
  const { t, i18n } = useTranslation('project');

  const items = useMemo<TabItem[]>(() => {
    const tabs: TabItem[] = [
      { to: 'overview', label: t('view.overview') },
      { to: 'files', label: t('view.files') },
    ];

    if (isAdmin) {
      tabs.push({ to: 'users', label: t('view.users') });
    }

    if (isAdminOfAnyTechCrew) {
      tabs.push({ to: 'techCrews', label: t('view.techCrews') });
    }

    if (isAdmin) {
      tabs.push({ to: 'settings', label: t('view.settings') });
    }

    return tabs;
  }, [t, isAdmin, isAdminOfAnyTechCrew]);

  const overflowTriggerLabel =
    OVERFLOW_TRIGGER_VARIANT === 'text' ? t('view.moreTabs') : '...';

  const measureKey = `${i18n.language}:${items
    .map((item) => item.label)
    .join(':')}`;

  const { visibleCount, setContainerEl, setMeasureEl, setOverflowMeasureEl } =
    useOverflowNav({
      itemCount: items.length,
      measureKey,
    });

  const visibleItems = items.slice(0, visibleCount);
  const overflowItems = items.slice(visibleCount);
  const hasOverflow = overflowItems.length > 0;

  return (
    <TabNavBar.Container>
      <div ref={setContainerEl} className="relative min-w-0">
        <div
          ref={setMeasureEl}
          aria-hidden
          className="pointer-events-none absolute -left-[9999px] top-0 flex whitespace-nowrap opacity-0"
        >
          {items.map((item) => (
            <span
              key={item.to}
              className={cn(tabNavBarLinkClassName(false), 'inline-flex')}
            >
              {item.label}
            </span>
          ))}
        </div>

        <div
          ref={setOverflowMeasureEl}
          aria-hidden
          className="pointer-events-none absolute -left-[9999px] top-0 opacity-0"
        >
          <span className={cn(tabNavBarLinkClassName(false), 'inline-flex')}>
            {overflowTriggerLabel}
          </span>
        </div>

        <div className="flex h-12 min-w-0 justify-start">
          {visibleItems.map((item) => (
            <TabNavBar.Link key={item.to} label={item.label} to={item.to} />
          ))}
          {hasOverflow && (
            <TabNavOverflowMenu
              items={overflowItems}
              triggerLabel={overflowTriggerLabel}
            />
          )}
        </div>
      </div>
    </TabNavBar.Container>
  );
};

const TabNavOverflowMenu = ({
  items,
  triggerLabel,
}: {
  items: TabItem[];
  triggerLabel: string;
}) => {
  const { t } = useTranslation('project');
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const isOverflowActive = items.some((item) =>
    isDashboardTabActive(location.pathname, item.to),
  );

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <Tippy
      content={
        <Menu className="text-shuttleGray-800">
          <Menu.List>
            {items.map((item) => (
              <OverflowMenuItem
                key={item.to}
                item={item}
                onSelect={() => setIsOpen(false)}
              />
            ))}
          </Menu.List>
        </Menu>
      }
      interactive
      visible={isOpen}
      onClickOutside={() => setIsOpen(false)}
      onHidden={() => setIsOpen(false)}
      trigger="manual"
      placement="bottom-start"
      arrow={false}
      appendTo={document.body}
      zIndex={60}
    >
      <button
        type="button"
        aria-label={t('view.moreTabs')}
        aria-expanded={isOpen}
        className={tabNavBarLinkClassName(isOverflowActive)}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {triggerLabel}
      </button>
    </Tippy>
  );
};

const OverflowMenuItem = ({
  item,
  onSelect,
}: {
  item: TabItem;
  onSelect: () => void;
}) => {
  const resolved = useResolvedPath(item.to);
  const match = useMatch({ path: `${resolved.pathname}/*` });

  return (
    <NavLink to={item.to} onClick={onSelect}>
      <Menu.Item selected={!!match}>{item.label}</Menu.Item>
    </NavLink>
  );
};
